import React, { useEffect, useState } from "react";
import { Box, useTheme } from "@mui/material";
import ShSwitch from "../../theme/inputs/ShSwitch";
import Text from "../../theme/text/Text";
import { useTranslation } from "react-i18next";
import { useUpdateSettingMutation, useUpdateSettingForRelationMutation } from "../../../newapi/global/generalApiSlice";
import ShThreeSwitch from "../../theme/inputs/ShThreeSwitch";
import EMGeneralExplainerBox from "../ModalsPopover/explainers/EMGeneralExplainerBox";
import { tokens } from "../../../theme";

// variants
//    1: default has both a title and subtitle
//    2: has no subtitle ans text is light
//
// manualSave: never persists on its own. The toggle still flips, and the new value is
//   reported through `onChange(value, setting)` for a screen that saves several settings
//   behind one Save button of its own, as useManualSettingsSave does.
//
// defaultValue: what a relation setting (relationId set) shows while it was never saved,
//   for settings the backend treats as on until someone switches them off.
const BoolSetting = (props) => {
  const { title = true, subtitle = true, variant = 1, disabled = false, manualSave = false } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [updateSetting] = useUpdateSettingMutation();
  const [updateSettingForRelation] = useUpdateSettingForRelationMutation();

  const [setting, setSetting] = useState();
  const [val, setVal] = useState();

  const handleBoxClick = () => {
    if (disabled) return;
    handleChange(val !== "true");
  };

  const handleChange = async (value) => {
    setVal(value ? "true" : "false");
    if (props.onChange) {
      props.onChange(value, props.relationId ? { ...setting, relationId: props.relationId } : setting);
    }
    // Manual-save toggles are persisted by the screen they sit on.
    if (manualSave) {
      return;
    }
    if(props.relationId){
      await updateSettingForRelation({
        key: setting.key,
        value: value,
        relationId: props.relationId
      });
    }else{
      await updateSetting({
        key: setting.key,
        value: value,
        warehouseId: setting.warehouseId,
      });
    }
  };

  const handleChangeUnset = async (value) => {
    const newValue = (value == 2) ? "true" : (value === 0) ? "false" : "unset";
    if (props.onChange) {
      props.onChange(newValue, props.relationId ? { ...setting, relationId: props.relationId } : setting);
    }

    setVal(newValue);
    if (manualSave) {
      return;
    }
    if(props.relationId){
      await updateSettingForRelation({
        key: setting.key,
        value: newValue,
        relationId: props.relationId
      });
    }else{
      await updateSetting({
        key: setting.key,
        value: newValue,
        warehouseId: setting.warehouseId,
      });
    }
  };

  useEffect(() => {
    if(props.skey && typeof props.settings !== "undefined"){
        let xsetting = props.settings.find((setting) => setting.key === props.skey);
        if (!xsetting) {
          if(props.relationId){
            xsetting = { key: props.skey, value: props.defaultValue ?? "unset", relationId: props.relationId };
          }else{
            xsetting = { key: props.skey, value: false, warehouseId: -1 };
          }
        }
        setSetting(xsetting);
        let xval = disabled ? false : xsetting.value;
        setVal(xval);
    }
  }, [props.settings, props.skey, props.relationId, props.defaultValue, disabled]);

  return (
    <Box
      sx={{
        width: 1,
      }}
    >
      <Box
        sx={{
          display:"none",
          "@media screen and (max-width: 47.9375em)": {
            display:"block"
          },
        }}
      >
        <Box
          sx={{
            ml:"-12px",
            mr:"-12px",
            pl:2,
            pr:2,
            display:"flex",
            // py:1,
            borderRadius:7,
            gap:2,
            alignItems:"center",
            justifyContent:"space-between",
            background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
            border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
            boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
          }}
        >
          <Text
            sx={props.explainer ? {display: "flex", alignItems: "center"} : {}} 
            noBreak 
            light={variant === 2} 
            size={variant === 2 ?? "fs13"} 
            semibold={variant === 1} >
              {t(`${setting?.key}_title`)}
              {props.explainer && <EMGeneralExplainerBox title={props.explainerTitle} text={props.explainerText}/>}
          </Text>
          <ShSwitch
            checked={val === "true"}
            disabled={disabled}
            onChange={(e) => {
              e.stopPropagation();
              if (!disabled) handleChange(e.target.checked);
            }}
            color={theme.palette.mode === "dark" ? "blue" : "primary"}
          />

        </Box>
        {subtitle && 
          <Text 
            light
            sx={{
              pt:1,
            }}
          >
            {t(`${setting?.key}_subtitle`)}
          </Text>
        }

      </Box>
      <Box
        sx={{
          width: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 4,
          cursor: disabled ? 'not-allowed' : 'pointer',
          "@media screen and (max-width: 47.9375em)": {
            display:"none"
          },
        }}
        onClick={handleBoxClick}
      >
        <Box
          sx={{
            display:"flex",
            flexDirection:"column",
            overflow:"hidden",
            cursor:"pointer"
          }}
        >
          {title && 
            <Text
              sx={props.explainer ? {display: "flex", alignItems: "center"} : {}} 
              noBreak 
              // light={variant === 2} 
              size={variant === 2 ?? "fs13"} 
              semibold={variant === 1} >
                {t(`${setting?.key}_title`)}
                {props.explainer && <EMGeneralExplainerBox title={props.explainerTitle} text={props.explainerText}/>}
            </Text>
          }
          {subtitle && variant !== 2 && <Text light>{t(`${setting?.key}_subtitle`)}</Text>}
        </Box>
        <ShSwitch
          checked={val === "true"}
          disabled={disabled}
          onChange={(e) => {
            e.stopPropagation();
            if (!disabled) handleChange(e.target.checked);
          }}
          color={theme.palette.mode === "dark" ? "blue" : "primary"}
        />
      </Box>
    </Box>
  );
};

export default BoolSetting;